import { useConfigStore } from '@/stores';
import { syncMapLanguage } from '@/utils/floor-guide';
import { getAllFloorsFromIndoorBuildings, getUniqueFloorsByKoreanName } from '@/utils/map';
import { DabeeoMap, DabeeoMapData } from 'dabeeomaps';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useFloor = (newMap?: DabeeoMap, mapData?: DabeeoMapData) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedFloorName, setSelectedFLoorName] = useState<string>('');

    const floorCode = useConfigStore((state) => state?.config?.server?.floor?.floorCode);
    const floorNameKo = useConfigStore((state) => state?.config?.server?.floor?.name?.ko);
    const appLanguage = useConfigStore((state) => state?.config?.ui?.language);

    const allFloors = useMemo(() => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        return getAllFloorsFromIndoorBuildings(buildings);
    }, [mapData?.dataBuilding]);

    const floors = useMemo(() => {
        return getUniqueFloorsByKoreanName(allFloors);
    }, [allFloors]);

    const getFloorsByBuildingId = useCallback((buildingId: string) => {
        const buildings = mapData?.dataBuilding?.getBuildings?.();
        if (!buildings) {
            return [];
        }

        return buildings.find((building) => buildingId.includes(building.id))?.floors || [];
    }, [mapData?.dataBuilding]);

    const handleChangeFloorByName = useCallback(
        async (floorName?: string) => {
            try {
                if (!newMap || !floorName) return;
                setLoading(true);
                await newMap.context?.changeFloorByName(floorName || '1F');
                syncMapLanguage(newMap, appLanguage);
                setSelectedFLoorName(floorName);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [newMap, appLanguage]
    );

    const handleChangeFloor = useCallback(
        async (floorId?: string) => {
            try {
                if (!newMap || !floorId) return;
                setLoading(true);
                await newMap.context?.changeFloor(floorId ?? '');
                syncMapLanguage(newMap, appLanguage);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        },
        [newMap, appLanguage]
    );

    const selectedFloor = useMemo(() => {
        if (!Array.isArray(floors) || !floors?.length) {
            return null;
        }
        return floors.find((floor) => floor?.name[0]?.text === selectedFloorName);
    }, [floors, selectedFloorName]);

    const getFloorById = useCallback(
        (floorId?: string) => {
            if (!Array.isArray(allFloors) || !allFloors?.length) {
                return undefined;
            }
            return allFloors.find((floor) => floor.id === floorId);
        },
        [allFloors]
    );

    useEffect(() => {
        if (!Array.isArray(floors)) {
            return;
        }
        if (floorCode) {
            setSelectedFLoorName(floorNameKo || '');
            return;
        }
        setSelectedFLoorName(floors.find((floor) => floor.defaultYn)?.name[0]?.text || '');
    }, [floors, floorCode, floorNameKo]);

    return {
        floors,
        handleChangeFloor,
        loading,
        selectedFloor,
        getFloorById,
        handleChangeFloorByName,
        selectedFloorName,
        setSelectedFLoorName,
        getFloorsByBuildingId
    };
};
